import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ArrowRight,
  BadgeCheck,
  Box,
  CheckCircle2,
  ChevronDown,
  CircleGauge,
  Cloud,
  Cuboid,
  Download,
  FileImage,
  Gauge,
  Layers3,
  LockKeyhole,
  Move3D,
  RefreshCw,
  ShieldCheck,
  Sparkles,
  UploadCloud,
  Wand2,
  Zap
} from 'lucide-react';
import StlPreview from './components/StlPreview.jsx';
import { generateStlWithApi, simulateStlGeneration } from './lib/mockGenerate.js';

const MAX_FILE_SIZE = 5 * 1024 * 1024;
const ALLOWED_TYPES = ['image/png', 'image/jpeg'];
const TURNSTILE_SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY || '1x00000000000000000000AA';

function Logo() {
  return (
    <a href="/" className="brand" aria-label="Image to STL Generator home">
      <span className="brand-mark">
        <Cuboid size={24} />
      </span>
      <span>imagetostl<span>.dev</span></span>
    </a>
  );
}

function AppHeader({ route, navigate }) {
  const navItems = [
    ['Features', 'features'],
    ['Examples', 'showcase'],
    ['FAQ', 'faq']
  ];

  return (
    <header className="site-header">
      <Logo />
      {route === 'home' ? (
        <nav className="main-nav" aria-label="Primary navigation">
          {navItems.map(([label, id]) => (
            <a href={`#${id}`} key={id}>
              {label}
            </a>
          ))}
        </nav>
      ) : (
        <div className="app-steps" aria-label="Generator progress">
          <span className="active">1 Upload & Configure</span>
          <ArrowRight size={16} />
          <span>2 Result</span>
        </div>
      )}
      <button className="header-cta" type="button" onClick={() => navigate('/app')}>
        {route === 'home' ? 'Start Generating For Free' : 'New Generation'}
      </button>
    </header>
  );
}

function HomePage({ navigate }) {
  return (
    <main>
      <section className="hero-section">
        <div className="hero-copy">
          <h1>Image to STL Generator</h1>
          <p>Turn any 2D image into a 3D STL model instantly. AI-powered photo to 3D print file generation for makers and 3D printing hobbyists.</p>
          <button className="primary-action hero-action" type="button" onClick={() => navigate('/app')}>
            Start Generating For Free
            <ArrowRight size={20} />
          </button>
          <div className="hero-feature-row" aria-label="Core advantages">
            <FeatureMini icon={<Move3D />} title="360° Full 3D AI Imagination" text="Complete watertight geometry from one image." />
            <FeatureMini icon={<Layers3 />} title="Auto Base Optimization" text="Smart leveling for print-ready bases." />
            <FeatureMini icon={<Gauge />} title="Dual-Speed Tracks" text="Fast previews or HD fidelity when detail matters." />
          </div>
        </div>
        <div className="hero-visual" aria-label="Input image and STL output comparison">
          <img
            src="/assets/image-to-stl-showcase.png"
            width="1402"
            height="1122"
            alt="Image to STL example showing a 2D robot image converted into a gray 3D printable STL model with a base"
          />
        </div>
      </section>

      <section className="feature-section" id="features" aria-labelledby="features-heading">
        <div className="section-heading">
          <h2 id="features-heading">Image to STL conversion built for printable results</h2>
          <p>Every image to STL generation path is tuned around closed geometry, base stability, and quick iteration.</p>
        </div>
        <div className="feature-grid">
          <FeatureCard
            icon={<Move3D />}
            title="360° Full 3D AI Imagination"
            text="The model does not stop at a shallow relief. The generator infers the hidden backside and creates a full-volume mesh."
          />
          <FeatureCard
            icon={<Layers3 />}
            title="Auto Base Optimization"
            text="Flat-bottom alignment and optional cylinder bases make the output easier to slice, place, and print."
          />
          <FeatureCard
            icon={<CircleGauge />}
            title="Dual-Speed Tracks"
            text="Use Fast Track for draft STL files, or HD Track when fine detail and higher mesh density matter."
          />
        </div>
      </section>

      <section className="visual-section" id="showcase" aria-labelledby="showcase-heading">
        <div className="visual-copy">
          <h2 id="showcase-heading">Convert image to STL with a browser preview</h2>
          <p>
            Upload a JPG or PNG, add short modification notes, then preview the R2-backed generated STL in-browser before downloading.
          </p>
          <button className="secondary-action" type="button" onClick={() => navigate('/app')}>
            Try the workstation
            <ArrowRight size={18} />
          </button>
        </div>
        <div className="showcase-card">
          <img
            src="/assets/preview.png"
            width="1431"
            height="1099"
            alt="Image to STL side-by-side example from 2D robot image to gray STL model with base"
          />
          <div className="showcase-caption">
            <span><FileImage size={16} /> JPG / PNG input</span>
            <span><Box size={16} /> STL mesh with base</span>
          </div>
        </div>
      </section>

      <section className="faq-section" id="faq" aria-labelledby="faq-heading">
        <div className="section-heading">
          <h2 id="faq-heading">FAQ</h2>
          <p>Quick answers for makers evaluating an image to STL workflow.</p>
        </div>
        <div className="faq-list">
          {[
            ['What is an image to STL generator?', 'An image to STL generator converts a 2D image or photo into an STL mesh file that can be checked in a slicer and used for 3D printing.'],
            ['Can I convert image to STL for 3D printing?', 'Yes. Upload a PNG or JPG, describe optional changes, choose Fast Track or HD Track, then preview and download the generated STL file.'],
            ['Are image to STL files print-ready?', 'The workflow targets watertight geometry with a stable base, but every STL should still be checked in your slicer before printing.'],
            ['What image formats are supported?', 'The current image to STL upload component accepts PNG and JPG images up to 5MB.'],
            ['How long does image to STL conversion take?', 'Fast Track is designed for quick previews under 1 minute. HD Track is designed for higher fidelity and typically takes 2 to 5 minutes.']
          ].map(([question, answer]) => (
            <details key={question} className="faq-item">
              <summary>
                <h3>{question}</h3>
                <ChevronDown size={20} />
              </summary>
              <p>{answer}</p>
            </details>
          ))}
        </div>
      </section>
    </main>
  );
}

function FeatureMini({ icon, title, text }) {
  return (
    <div className="feature-mini">
      <span>{icon}</span>
      <strong>{title}</strong>
      <small>{text}</small>
    </div>
  );
}

function FeatureCard({ icon, title, text }) {
  return (
    <article className="feature-card">
      <span className="feature-icon">{icon}</span>
      <h3>{title}</h3>
      <p>{text}</p>
    </article>
  );
}

function UploadPanel({ file, previewUrl, onFileSelected, error }) {
  const inputRef = useRef(null);
  const [dragging, setDragging] = useState(false);

  const handleFiles = useCallback(
    (files) => {
      const nextFile = files?.[0];
      if (nextFile) onFileSelected(nextFile);
    },
    [onFileSelected]
  );

  return (
    <section className="input-block" aria-labelledby="upload-heading">
      <div className="block-title">
        <span>1</span>
        <h2 id="upload-heading">Upload Image</h2>
      </div>
      <div className="upload-layout">
        <button
          className={`dropzone ${dragging ? 'dragging' : ''}`}
          type="button"
          onClick={() => inputRef.current?.click()}
          onDragEnter={(event) => {
            event.preventDefault();
            setDragging(true);
          }}
          onDragOver={(event) => event.preventDefault()}
          onDragLeave={() => setDragging(false)}
          onDrop={(event) => {
            event.preventDefault();
            setDragging(false);
            handleFiles(event.dataTransfer.files);
          }}
        >
          <UploadCloud size={44} />
          <strong>Drag & drop an image here</strong>
          <span>or click to upload</span>
          <small>PNG / JPG · Max 5MB</small>
        </button>
        <input
          ref={inputRef}
          type="file"
          accept="image/png,image/jpeg"
          onChange={(event) => handleFiles(event.target.files)}
          hidden
        />
        <div className={`thumb-card ${previewUrl ? 'has-image' : ''}`}>
          {previewUrl ? (
            <>
              <img src={previewUrl} alt={`Preview of ${file.name}`} />
              <div>
                <strong>{file.name}</strong>
                <span>{(file.size / 1024 / 1024).toFixed(2)} MB</span>
              </div>
            </>
          ) : (
            <div className="thumb-empty">
              <FileImage size={34} />
              <span>No image selected</span>
            </div>
          )}
        </div>
      </div>
      {error && <p className="field-error">{error}</p>}
    </section>
  );
}

function ModeSelector({ mode, setMode }) {
  const modes = [
    {
      id: 'fast',
      title: 'Fast Track',
      time: '< 1 minute',
      text: 'Quick previews and simpler geometry.',
      icon: <Zap size={28} />
    },
    {
      id: 'hd',
      title: 'HD Track',
      time: '2~5 minutes',
      text: 'Higher detail for complex objects.',
      icon: <Sparkles size={28} />
    }
  ];

  return (
    <section className="input-block" aria-labelledby="mode-heading">
      <div className="block-title">
        <span>3</span>
        <h2 id="mode-heading">Select Generation Mode</h2>
      </div>
      <div className="mode-grid" role="radiogroup" aria-label="Generation mode">
        {modes.map((item) => (
          <button
            key={item.id}
            type="button"
            role="radio"
            aria-checked={mode === item.id}
            className={`mode-card ${mode === item.id ? 'selected' : ''}`}
            onClick={() => setMode(item.id)}
          >
            <span className="radio-dot" />
            <span className="mode-icon">{item.icon}</span>
            <strong>{item.title}</strong>
            <small>{item.time}</small>
            <p>{item.text}</p>
          </button>
        ))}
      </div>
    </section>
  );
}

function TurnstileBox({ verified, setVerified }) {
  const boxRef = useRef(null);
  const widgetRef = useRef(null);
  const verifiedRef = useRef(verified);
  const [loadState, setLoadState] = useState('loading');

  useEffect(() => {
    verifiedRef.current = verified;
  }, [verified]);

  useEffect(() => {
    window.__imageToStlTurnstileSuccess = () => {
      setVerified(true);
      setLoadState('ready');
    };
    return () => {
      delete window.__imageToStlTurnstileSuccess;
    };
  }, [setVerified]);

  useEffect(() => {
    let retryTimer;
    let localFallback;
    const localDefaultVerification =
      ['localhost', '127.0.0.1'].includes(window.location.hostname) && !import.meta.env.VITE_TURNSTILE_SITE_KEY;

    if (localDefaultVerification) {
      localFallback = window.setTimeout(() => {
        if (!verifiedRef.current) {
          setLoadState('ready');
          setVerified(true);
        }
      }, 700);

      return () => {
        window.clearTimeout(localFallback);
      };
    }

    function renderTurnstile() {
      if (!window.turnstile || !boxRef.current || widgetRef.current) return false;
      widgetRef.current = window.turnstile.render(boxRef.current, {
        sitekey: TURNSTILE_SITE_KEY,
        theme: 'light',
        action: 'generate_stl',
        callback: window.__imageToStlTurnstileSuccess,
        'expired-callback': () => setVerified(false),
        'error-callback': () => setLoadState('error')
      });
      return true;
    }

    if (!window.turnstile) {
      let script = document.querySelector('script[data-turnstile="true"]');
      if (!script) {
        script = document.createElement('script');
        script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
        script.async = true;
        script.defer = true;
        script.dataset.turnstile = 'true';
        document.head.appendChild(script);
      }
      script.addEventListener('load', renderTurnstile);
      script.addEventListener('error', () => setLoadState('error'));
    }

    if (!renderTurnstile()) {
      retryTimer = window.setInterval(() => {
        if (renderTurnstile()) {
          window.clearInterval(retryTimer);
        }
      }, 300);
    }

    localFallback = window.setTimeout(() => {
      if (!verifiedRef.current && window.location.hostname === 'localhost') {
        setLoadState('ready');
        setVerified(true);
      }
    }, 2600);

    return () => {
      window.clearInterval(retryTimer);
      window.clearTimeout(localFallback);
      if (window.turnstile && widgetRef.current) {
        window.turnstile.remove(widgetRef.current);
        widgetRef.current = null;
      }
    };
  }, [setVerified]);

  return (
    <section className="input-block" aria-labelledby="turnstile-heading">
      <div className="block-title">
        <span>4</span>
        <h2 id="turnstile-heading">Verify You&apos;re Human</h2>
      </div>
      <div className={`turnstile-wrap ${verified ? 'verified' : ''}`}>
        <div ref={boxRef} className="turnstile-widget" />
        {verified && (
          <div className="turnstile-fallback">
            <CheckCircle2 size={24} />
            <span>Verification complete</span>
          </div>
        )}
        {!verified && loadState === 'loading' && <span className="turnstile-loading">Loading Cloudflare Turnstile...</span>}
        {!verified && loadState === 'error' && <span className="turnstile-loading">Turnstile could not load. Try refreshing.</span>}
      </div>
    </section>
  );
}

function ProcessingPanel({ status, progress, message }) {
  return (
    <div className={`processing-panel ${status === 'processing' ? 'is-processing' : ''}`}>
      <div className="processing-icon">
        {status === 'completed' ? <BadgeCheck size={30} /> : <Wand2 size={30} />}
      </div>
      <div>
        <strong>{message || 'Ready for a new generation.'}</strong>
        <div className="progress-track" aria-label={`Generation progress ${progress}%`}>
          <span style={{ width: `${progress}%` }} />
        </div>
        <small>{status === 'completed' ? 'Completed with a mock STL file for local development.' : 'Keep this page open while the async job runs.'}</small>
      </div>
      <span className="progress-number">{progress}%</span>
    </div>
  );
}

function WorkstationPage() {
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [uploadError, setUploadError] = useState('');
  const [prompt, setPrompt] = useState('');
  const [mode, setMode] = useState('fast');
  const [verified, setVerified] = useState(false);
  const [status, setStatus] = useState('idle');
  const [progress, setProgress] = useState(0);
  const [message, setMessage] = useState('Ready to convert your image to STL.');
  const [result, setResult] = useState(null);

  const revokeResultUrl = useCallback((nextResult) => {
    if (nextResult?.objectUrl && nextResult.url) URL.revokeObjectURL(nextResult.url);
    if (nextResult?.objectUrl && nextResult.downloadUrl && nextResult.downloadUrl !== nextResult.url) {
      URL.revokeObjectURL(nextResult.downloadUrl);
    }
  }, []);

  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      revokeResultUrl(result);
    };
  }, [previewUrl, result, revokeResultUrl]);

  const onFileSelected = useCallback(
    (nextFile) => {
      setUploadError('');
      if (!ALLOWED_TYPES.includes(nextFile.type)) {
        setUploadError('Please upload a PNG or JPG image.');
        return;
      }
      if (nextFile.size > MAX_FILE_SIZE) {
        setUploadError('Image must be 5MB or smaller.');
        return;
      }
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      setFile(nextFile);
      setPreviewUrl(URL.createObjectURL(nextFile));
      setResult(null);
      setStatus('idle');
      setProgress(0);
      setMessage('Ready to convert your image to STL.');
    },
    [previewUrl]
  );

  const canGenerate = useMemo(() => Boolean(file && verified && status !== 'processing'), [file, verified, status]);

  async function handleGenerate() {
    if (!file) {
      setUploadError('Upload an image before generating STL.');
      return;
    }
    if (!verified) {
      setMessage('Complete Cloudflare Turnstile verification first.');
      return;
    }

    revokeResultUrl(result);
    setStatus('processing');
    setProgress(4);
    setMessage('Starting edge API job...');
    setResult(null);

    const onProgress = ({ progress: nextProgress, message: nextMessage }) => {
      setProgress(nextProgress);
      setMessage(nextMessage);
    };
    const isLocal = ['localhost', '127.0.0.1'].includes(window.location.hostname);

    try {
      const generated = isLocal
        ? await simulateStlGeneration({ file, mode, onProgress })
        : await generateStlWithApi({ file, prompt, mode, onProgress });

      setResult(generated);
      setStatus('completed');
      setProgress(100);
      setMessage(generated.storage === 'cloudflare-r2' ? 'Final STL is ready in Cloudflare R2.' : 'Final STL is ready.');
    } catch (error) {
      setStatus('error');
      setProgress(0);
      setMessage(error instanceof Error ? error.message : 'Generation failed. Please try again.');
    }
  }

  function resetGeneration() {
    revokeResultUrl(result);
    setResult(null);
    setStatus('idle');
    setProgress(0);
    setMessage('Ready to convert your image to STL.');
  }

  return (
    <main className="workstation">
      <section className="input-panel" aria-label="Image to STL inputs">
        <UploadPanel file={file} previewUrl={previewUrl} onFileSelected={onFileSelected} error={uploadError} />
        <section className="input-block" aria-labelledby="mod-heading">
          <div className="block-title">
            <span>2</span>
            <h2 id="mod-heading">Describe Your Modification</h2>
          </div>
          <textarea
            value={prompt}
            onChange={(event) => setPrompt(event.target.value.slice(0, 500))}
            placeholder="Describe your modification requirements (e.g., 'Add a cylinder base', 'Make the tail longer')."
            maxLength={500}
          />
          <div className="char-count">{prompt.length} / 500</div>
        </section>
        <ModeSelector mode={mode} setMode={setMode} />
        <TurnstileBox verified={verified} setVerified={setVerified} />
        <button className="generate-button" type="button" disabled={!canGenerate} onClick={handleGenerate}>
          <Wand2 size={22} />
          Generate STL
        </button>
        <div className="secure-note">
          <ShieldCheck size={18} />
          <span>Secure edge workflow · temporary files expire after 24 hours.</span>
        </div>
      </section>

      <section className="result-panel" aria-label="STL generation result">
        <div className="result-title">
          <div>
            <span>2</span>
            <h1>Result</h1>
          </div>
          <strong className={`result-state ${status}`}>
            {status === 'completed' ? 'Completed' : status === 'processing' ? 'Processing' : status === 'error' ? 'Error' : 'Waiting'}
          </strong>
        </div>
        <ProcessingPanel status={status} progress={progress} message={message} />
        <div className="preview-header">
          <div>
            <h2>Your STL Preview</h2>
            <p>WebGL preview loads after the generated STL is available.</p>
          </div>
          {result && (
            <dl className="mesh-stats">
              <div>
                <dt>Mode</dt>
                <dd>{result.stats.mode}</dd>
              </div>
              <div>
                <dt>Faces</dt>
                <dd>{result.stats.faces}</dd>
              </div>
              <div>
                <dt>TTL</dt>
                <dd>{result.stats.ttl}</dd>
              </div>
            </dl>
          )}
        </div>
        <StlPreview stlUrl={result?.url} status={status} />
        <div className="result-actions">
          <a
            className={`download-button ${!result ? 'disabled' : ''}`}
            href={result?.downloadUrl || result?.url || '#'}
            download={result?.fileName}
            aria-disabled={!result}
            onClick={(event) => {
              if (!result) event.preventDefault();
            }}
          >
            <Download size={22} />
            Download STL File
          </a>
          <button className="secondary-action action-outline" type="button" onClick={resetGeneration}>
            <RefreshCw size={19} />
            New Generation
          </button>
        </div>
        <div className="edge-map" aria-label="Cloudflare deployment architecture">
          <span><Cloud size={18} /> Pages</span>
          <ArrowRight size={16} />
          <span>Workers API</span>
          <ArrowRight size={16} />
          <span>Queue</span>
          <ArrowRight size={16} />
          <span>R2 STL Cache</span>
        </div>
      </section>
    </main>
  );
}

export default function App() {
  const [path, setPath] = useState(window.location.pathname);

  const navigate = useCallback((nextPath) => {
    window.history.pushState({}, '', nextPath);
    setPath(nextPath);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  useEffect(() => {
    const onPopState = () => setPath(window.location.pathname);
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  const route = path.startsWith('/app') ? 'app' : 'home';

  return (
    <div className="page-shell">
      <AppHeader route={route} navigate={navigate} />
      {route === 'home' ? <HomePage navigate={navigate} /> : <WorkstationPage />}
      {route === 'home' && (
        <footer className="site-footer">
          <Logo />
          <span>Image to STL, picture to STL, and photo to 3D model generation for makers.</span>
          <span><LockKeyhole size={16} /> Built for Cloudflare Pages, Workers, CDN, R2, and Turnstile.</span>
        </footer>
      )}
    </div>
  );
}
